import React, { createContext, useState, useCallback, useContext } from 'react';
import getPlanById from '../../../domain/use_cases/plans/getPlanById';
import searchPlans from '../../../domain/use_cases/plans/searchPlans';
import { IConstraint, IPlan } from '@leedpulse-monorepo/interfaces';
import AuthenticationContext from '../auth/AuthenticationContext';
import listLeedflowProductsWithPlans from '../../../domain/use_cases/stripe/listLeedflowProductsWithPlans';
import { Currency } from '@leedpulse-monorepo/enums';
import CompanyContext from '../company/CompanyContext';

interface PlansContextValue {
  planCache: Map<string, IPlan>;
  plans: IPlan[];
  /** `fetchPlans` walks the Stripe catalogue then reads each plan one by one: it takes seconds */
  plansLoading: boolean;
  fetchPlanById: (planId: string) => Promise<IPlan | null>;
  fetchPlans: (constraints?: IConstraint[], ignoreCache?: boolean) => Promise<void>;
}

const PlansContext = createContext<PlansContextValue>({
  planCache: new Map(),
  plans: [],
  plansLoading: false,
  fetchPlanById: async () => null,
  fetchPlans: async () => { return; },
});

export const PlansProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [planCache, setPlanCache] = useState<Map<string, IPlan>>(new Map());
  const [plans, setPlans] = useState<IPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  
  const authContext = useContext(AuthenticationContext);
  const companyContext = useContext(CompanyContext);

  // Return 1 Plan from an ID
  const fetchPlanById = useCallback(
    async (planId: string): Promise<IPlan | null> => {
      // Check cache first
      if (planCache.has(planId)) {
        return planCache.get(planId) || null;
      }

      try {
        const plan = await getPlanById(planId);
        if (plan) {
          setPlanCache((prev) => new Map(prev).set(planId, plan));
        }
        return plan;
      } catch (error) {
        console.error('Error fetching plan:', error);
        return null;
      }
    },
    [planCache]
  );

  // Fetch all Plans by constraints (now backed by Algolia search)
  const fetchPlans = useCallback(
    async (constraints: IConstraint[] = [], ignoreCache = false): Promise<void> => {
      setPlansLoading(true);
      try {
        // Use Stripe real prices (same route as backoffice).
        if (authContext.authentication?.token && companyContext.activeCompany) {
          const catalog = await listLeedflowProductsWithPlans(companyContext.activeCompany.api_key ?? '', authContext.authentication, {
            product_status: 'active',
            visibility: 'public',
          });

          const priceByPlanId = new Map<string, { value: number; formatted: string; currency: Currency; stripe_price_id: string }>();

          for (const product of catalog?.products ?? []) {
            for (const price of product?.prices ?? []) {
              const unitAmount = Number(price?.unit_amount ?? 0);
              const currencyRaw = String(price?.currency ?? 'EUR').toUpperCase();
              const currency =
                currencyRaw === Currency.EUR || currencyRaw === Currency.USD || currencyRaw === Currency.GBP
                  ? (currencyRaw as Currency)
                  : Currency.EUR;

              for (const plan of price?.plans ?? []) {
                if (!plan?.id || plan.has_valid_stripe_price !== true) continue;
                if (plan.archived) continue; 
                priceByPlanId.set(plan.id, {
                  value: unitAmount / 100,
                  formatted: price.formatted_amount ?? '',
                  currency,
                  stripe_price_id: price.id,
                });
              }
            }
          }

          const nextPlans: IPlan[] = [];
          for (const [planId, stripePrice] of priceByPlanId.entries()) {
            const plan = await getPlanById(planId);
            if (!plan) continue;

            nextPlans.push({
              ...plan,
              price: {
                ...plan.price,
                value: stripePrice.value,
                formatted: stripePrice.formatted,
                currency: stripePrice.currency ?? plan.price.currency,
                stripe_price_id: stripePrice.stripe_price_id,
              },
            });
          }

          nextPlans.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));

          setPlans(nextPlans);
          setPlanCache(() => {
            const next = new Map<string, IPlan>();
            nextPlans.forEach((p) => next.set(p.id, p));
            return next;
          });
          return;
        }
        console.error('No authentication or company found');

        // Fallback (algolia)
        const searchedPlans = await searchPlans(authContext.authentication);
        setPlans(searchedPlans);
        setPlanCache(() => {
          const next = new Map<string, IPlan>();
          searchedPlans.forEach((plan) => next.set(plan.id, plan));
          return next;
        });
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setPlansLoading(false);
      }
    },
    [authContext.authentication, companyContext.activeCompany]
  );

  return (
    <PlansContext.Provider value={{
      planCache,
      plans,
      plansLoading,
      fetchPlanById,
      fetchPlans
    }}>{children}</PlansContext.Provider>
  );
};

export default PlansContext;
